import type { WalletContextState } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import { canonicalizeArweaveGatewayUrl } from '../../shared/arweave/publicArweaveGateway';
import { type Either, isLeft } from 'fp-ts/Either';
import type { TRPC } from '../../contexts/TRPC';
import { isUserRejectedError } from '../errorHandling';
import { mintReleaseNftWithRetry } from '../releaseMinting';
import { deduplicateTransactionSigners } from '../transactionHelpers';

type TRPCClient = TRPC['client'];

type TrpcLeft = { message?: string };

function unwrapEither<T>(
  result: Either<TrpcLeft, T>,
  fallbackMessage: string,
): T {
  if (isLeft(result)) {
    throw new Error(result.left.message || fallbackMessage);
  }
  return result.right;
}

function ensureWalletCanSignTransactions(
  wallet: WalletContextState,
  notConnectedMessage = 'Wallet not connected',
): asserts wallet is WalletContextState & {
  publicKey: NonNullable<WalletContextState['publicKey']>;
  signTransaction: NonNullable<WalletContextState['signTransaction']>;
} {
  if (!wallet.connected || !wallet.publicKey || !wallet.signTransaction) {
    throw new Error(notConnectedMessage);
  }
}

export async function signAndSubmitPreparedNftTransaction(params: {
  wallet: WalletContextState;
  trpc: TRPCClient;
  serializedTransaction: string;
  mintAddress?: string;
}): Promise<string> {
  const { wallet, trpc, serializedTransaction, mintAddress } = params;

  ensureWalletCanSignTransactions(
    wallet,
    'Wallet does not support transaction signing',
  );

  const transaction = Transaction.from(
    Buffer.from(serializedTransaction, 'base64'),
  );

  deduplicateTransactionSigners(transaction, mintAddress);

  let signedTransaction: Transaction;
  try {
    signedTransaction = await wallet.signTransaction(transaction);
  } catch (signError) {
    if (isUserRejectedError(signError)) {
      throw new Error('User rejected the request');
    }
    throw signError;
  }

  const submitResult = unwrapEither(
    await trpc.submitNftTransaction.mutate({
      signedTransaction: signedTransaction.serialize().toString('base64'),
    }),
    'Failed to submit NFT transaction',
  );

  return submitResult.transactionSignature;
}

export interface AppMintWorkflowParams {
  wallet: WalletContextState;
  trpc: TRPCClient;
  uploadAppNftData: (
    wallet: WalletContextState,
    appData: {
      name: string;
      website?: string;
      androidPackage: string;
      address: string;
    },
    iconFile: File,
    trpc: TRPCClient,
    onProgress?: (
      progress: number,
      message?: string,
      isSignatureInProgress?: boolean,
    ) => void,
  ) => Promise<{ metadataUri: string }>;
  app: {
    dappId: string;
    name: string;
    website?: string | null;
    androidPackage: string;
    publisherAddress: string;
  };
  iconFile: File;
  onStatus?: (status: string) => void;
  onUploadProgress?: (
    progress: number,
    message?: string,
    isSignatureInProgress?: boolean,
  ) => void;
}

export interface AppMintWorkflowResult {
  mintAddress: string;
  transactionSignature: string;
  metadataUri: string;
}

export async function mintAppNftWorkflow(
  params: AppMintWorkflowParams,
): Promise<AppMintWorkflowResult> {
  const {
    wallet,
    trpc,
    uploadAppNftData,
    app,
    iconFile,
    onStatus,
    onUploadProgress,
  } = params;

  ensureWalletCanSignTransactions(wallet);

  const connectedAddress = wallet.publicKey.toBase58();
  if (app.publisherAddress && connectedAddress !== app.publisherAddress) {
    throw new Error(
      `Wrong wallet connected. Expected: ${app.publisherAddress}`,
    );
  }

  onStatus?.('Uploading to permanent storage...');

  const uploadResult = await uploadAppNftData(
    wallet,
    {
      name: app.name,
      website: app.website || '',
      androidPackage: app.androidPackage,
      address: connectedAddress,
    },
    iconFile,
    trpc,
    onUploadProgress,
  );
  const metadataUri = canonicalizeArweaveGatewayUrl(uploadResult.metadataUri);

  onStatus?.('Preparing NFT transaction...');

  const preparedTx = unwrapEither(
    await trpc.prepareAppNftTransaction.mutate({
      appName: app.name,
      // Backend field name is historical; it expects the metadata URI.
      appIcon: metadataUri,
      appWebsite: app.website || undefined,
      androidPackage: app.androidPackage,
      publisherAddress: app.publisherAddress,
      payerAddress: connectedAddress,
    }),
    'Failed to prepare app NFT transaction',
  );
  const preparedMetadataUri = canonicalizeArweaveGatewayUrl(
    preparedTx.metadataUri,
  );

  onStatus?.('Signing and submitting transaction...');

  const transactionSignature = await signAndSubmitPreparedNftTransaction({
    wallet,
    trpc,
    serializedTransaction: preparedTx.transaction,
    mintAddress: preparedTx.mintAddress,
  });

  onStatus?.('Saving NFT data...');

  unwrapEither(
    await trpc.saveAppNftData.mutate({
      dappId: app.dappId,
      mintAddress: preparedTx.mintAddress,
      transactionSignature,
      metadataUri: preparedMetadataUri,
      ownerAddress: connectedAddress,
      walletAddress: connectedAddress,
      appName: app.name,
      androidPackage: app.androidPackage,
    }),
    'Failed to save app NFT data',
  );

  return {
    mintAddress: preparedTx.mintAddress,
    transactionSignature,
    metadataUri: preparedMetadataUri,
  };
}

export interface ReleaseMintWorkflowParams {
  wallet: WalletContextState;
  trpc: TRPCClient;
  release: {
    releaseId: string;
    releaseName: string;
    releaseVersion: string;
    releaseMetadataUri: string;
    androidPackage: string;
    appMintAddress: string;
    publisherAddress: string;
  };
  uploadProvider?: 'Arweave' | 'S3' | 'R2' | 'IPFS';
  uploadProviderId?: string;
}

export interface ReleaseMintWorkflowResult {
  mintAddress: string;
  transactionSignature: string;
  metadataUri: string;
}

export async function mintReleaseNftWorkflow(
  params: ReleaseMintWorkflowParams,
): Promise<ReleaseMintWorkflowResult> {
  const { wallet, trpc, release, uploadProvider, uploadProviderId } = params;

  ensureWalletCanSignTransactions(wallet);
  const releaseMetadataUri = canonicalizeArweaveGatewayUrl(
    release.releaseMetadataUri,
  );

  const releaseNameForMint =
    release.releaseName.length > 32
      ? release.releaseName.substring(0, 32)
      : release.releaseName;

  const { preparedTx, transactionSignature } = await mintReleaseNftWithRetry({
    trpc,
    wallet,
    releaseId: release.releaseId,
    releaseName: releaseNameForMint,
    releaseMetadataUri,
    appMintAddress: release.appMintAddress,
    publisherAddress: release.publisherAddress,
  });
  const preparedMetadataUri = canonicalizeArweaveGatewayUrl(
    preparedTx.metadataUri,
  );

  unwrapEither(
    await trpc.release.saveReleaseNftData.mutate({
      releaseId: release.releaseId,
      mintAddress: preparedTx.mintAddress,
      transactionSignature,
      metadataUri: preparedMetadataUri,
      ownerAddress: wallet.publicKey.toBase58(),
      releaseName: release.releaseName,
      releaseVersion: release.releaseVersion,
      androidPackage: release.androidPackage,
      appMintAddress: release.appMintAddress,
      uploadProvider,
      uploadProviderId,
    }),
    'Failed to save release NFT data',
  );

  return {
    mintAddress: preparedTx.mintAddress,
    transactionSignature,
    metadataUri: preparedMetadataUri,
  };
}
